import React, { useLayoutEffect, useRef, useState } from "react";
import { FiArrowRight, FiPaperclip, FiX, FiLoader } from "react-icons/fi";
import { ReactComponent as GlobeClick } from "../../../assets/icons/globe-06.svg";
import { MCPService, AttachedFile } from "../../../services/mcpService";
import AtMentionPicker, { renderMentionChipInner } from "./AtMentionPicker";
import MentionEditor, { MentionEditorHandle } from "./MentionEditor";
import { MentionedAsset } from "../hooks/useAtMention";
import useOutsideClick from "../../../hooks/useOutsideClick";

/** One measure for the composer wherever it renders — pinned at the bottom, or under the empty state. */
export const CHAT_INPUT_MEASURE = "max-w-[680px]";

// Attach and map sit beside the Send pill, so they take its round shape and height rather than reading as a different family of control
const ACTION_BUTTON = "eai-btn-toolbar-icon flex-shrink-0";

// This component renders the input area for the AI chat feature.
interface AiChatInputProps {
  inputRef: React.RefObject<HTMLTextAreaElement>;
  inputValue: string;
  onInputChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  isLoading: boolean;
  error: string | null;
  variant?: "panel" | "analyst";
  placeholder?: string;
  containerClassName?: string;
  textareaSizeClassName?: string;
  disabled?: boolean;
  disabledMessage?: string;
  attachedFiles?: AttachedFile[];
  isUploading?: boolean;
  onFileSelect?: (files: FileList) => void;
  onRemoveFile?: (uri: string) => void;
  onMapDraw?: () => void;
  // When true, the Send button is omitted (e.g. the schedule modal drives submission itself)
  hideSend?: boolean;
  // Mention props
  mentionedAssets?: MentionedAsset[];
  isMentionPickerOpen?: boolean;
  mentionQuery?: string;
  mentionSuggestions?: MentionedAsset[];
  onMentionSelect?: (asset: MentionedAsset) => void;
  // Removes a mention chip — only used by plain-textarea surfaces; the Lexical editor renders mentions as inline pills deleted with the text itself.
  onMentionRemove?: (assetId: string) => void;
  onMentionPickerClose?: () => void;
  // Editor props — when these are provided the plain textarea is replaced by the Lexical MentionEditor, which renders tagged assets/groups as inline pills.
  editorRef?: React.Ref<MentionEditorHandle>;
  onEditorChange?: (value: string, caretOffset: number) => void;
  resolveMentionEntity?: (label: string) => MentionedAsset | undefined;
  onCursorMove?: () => void;
  // Anchor for the new-user tour spotlight (data-tour attribute on the input box).
  dataTour?: string;
}
// AiChatInput component definition
const AiChatInput: React.FC<AiChatInputProps> = ({
  inputRef,
  inputValue,
  onInputChange,
  onKeyDown,
  onSend,
  isLoading,
  error,
  variant = "panel",
  placeholder,
  containerClassName,
  textareaSizeClassName = "min-h-[24px] max-h-40 sm:max-h-48",
  disabled = false,
  disabledMessage,
  attachedFiles = [],
  isUploading = false,
  onFileSelect,
  onRemoveFile,
  onMapDraw,
  hideSend = false,
  mentionedAssets = [],
  isMentionPickerOpen = false,
  mentionQuery = "",
  mentionSuggestions = [],
  onMentionSelect,
  onMentionRemove,
  onMentionPickerClose,
  editorRef,
  onEditorChange,
  resolveMentionEntity,
  onCursorMove,
  dataTour,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputBoxRef = useRef<HTMLDivElement>(null);
  const dragCounterRef = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);

  useOutsideClick(inputBoxRef, () => onMentionPickerClose?.());

  // Grow the textarea with its text, then let max-h cap it and scroll inside
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [inputValue, inputRef]);

  const isSendDisabled = (!inputValue.trim() && mentionedAssets.length === 0) || isLoading || disabled || isUploading;

  // Mention-enabled surfaces render the Lexical inline-pill editor instead of the textarea.
  const useMentionEditor = Boolean(onEditorChange && resolveMentionEntity);
  // While the picker is open with suggestions it owns Enter/Tab/arrows; the editor preventDefaults and the picker's window keydown listener performs the action.
  const handlePickerKey = () => isMentionPickerOpen && mentionSuggestions.length > 0;

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0 && onFileSelect) {
      onFileSelect(files);
    }
    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files') || disabled || !onFileSelect) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files') || disabled || !onFileSelect) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0 && onFileSelect && !disabled) {
      onFileSelect(files);
    }
  };

  return (
    <div className={containerClassName ?? "px-3 pb-4"}>
      <div
        ref={inputBoxRef}
        data-tour={dataTour}
        className={`relative flex flex-col rounded-eai-card border bg-eai-surface-card p-2.5 shadow-eai-card transition-colors sm:p-4 ${
          isDragOver
            ? 'border-eai-cta-primary bg-eai-surface-sunken'
            : 'border-eai-border'
        }`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drop overlay */}
        {isDragOver && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-eai-card bg-eai-surface-sunken/80">
            <p className="text-eai-body-sm font-medium text-eai-cta-primary">Drop files here</p>
          </div>
        )}

        {/* Asset mention picker */}
        {onMentionSelect && onMentionPickerClose && (
          <AtMentionPicker
            isOpen={isMentionPickerOpen}
            assets={mentionSuggestions}
            query={mentionQuery}
            onSelect={onMentionSelect}
            onClose={onMentionPickerClose}
          />
        )}

        {/* Selected @-mention chips — plain-textarea surfaces only; the Lexical editor shows mentions as inline pills within the text instead. */}
        {!useMentionEditor && mentionedAssets.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {mentionedAssets.map((asset) => (
              <div
                key={asset.id}
                className="font-outfit flex items-center gap-1.5 rounded-full border border-eai-cta-primary/25 bg-eai-checklist-lt px-3 py-1 text-sm text-eai-cta-primary"
              >
                {renderMentionChipInner(asset, asset.groupName, {
                  size: 16,
                  groupNameClass: "opacity-70",
                  nameClass: "font-medium max-w-[150px] truncate",
                })}
                {onMentionRemove && (
                  <button
                    type="button"
                    onClick={() => onMentionRemove(asset.id)}
                    className="ml-1 flex h-4 w-4 items-center justify-center rounded-full transition hover:bg-eai-cta-primary/20"
                    aria-label={`Remove ${asset.name}`}
                  >
                    <FiX className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Input area with icon */}
        <div className="flex items-center gap-2 text-eai-ink-subtle">
          {useMentionEditor ? (
            <MentionEditor
              ref={editorRef}
              value={inputValue}
              onChange={onEditorChange!}
              onSend={hideSend ? undefined : onSend}
              onPickerKey={handlePickerKey}
              resolveEntity={resolveMentionEntity!}
              disabled={disabled}
              placeholder={disabled ? disabledMessage : (placeholder ?? "What do you want to know?")}
              className={textareaSizeClassName}
            />
          ) : (
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={onKeyDown}
              onKeyUp={onCursorMove}
              onClick={onCursorMove}
              disabled={disabled}
              className={`font-inter flex-1 resize-none overflow-y-auto bg-transparent text-eai-body text-eai-ink placeholder:text-eai-ink-disabled focus:outline-none ${textareaSizeClassName} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              placeholder={disabled ? disabledMessage : (placeholder ?? "What do you want to know?")}
              rows={1}
            />
          )}
        </div>
        {/* Bottom row: attached files on the left, action buttons on the right. flex-wrap keeps the files on this line beside the buttons, and lets them float to their own line above only when there isn't room. */}
        <div className="flex flex-wrap items-center gap-2 pt-2 sm:pt-3">
          {attachedFiles.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {variant === "analyst" && (
                <span className="eai-meta-label">
                  Attached Data
                </span>
              )}
              {attachedFiles.map((file) => (
                <div
                  key={file.uri}
                  className="eai-tag-pill-sm gap-1.5 py-1 text-sm text-eai-ink"
                >
                  <FiPaperclip className="h-3 w-3 text-eai-ink-subtle" />
                  <span className="max-w-[150px] truncate">{file.filename}</span>
                  <span className="text-eai-ink-subtle">({MCPService.formatFileSize(file.size)})</span>
                  {onRemoveFile && (
                    <button
                      type="button"
                      onClick={() => onRemoveFile(file.uri)}
                      className="ml-1 flex h-4 w-4 items-center justify-center rounded-full transition hover:bg-eai-surface-active"
                      aria-label={`Remove ${file.filename}`}
                    >
                      <FiX className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
              {isUploading && (
                <div className="eai-tag-pill-sm gap-1.5 py-1 text-sm">
                  <FiLoader className="h-3 w-3 animate-spin" />
                  <span>Uploading...</span>
                </div>
              )}
            </div>
          )}
          <div className="ml-auto flex flex-shrink-0 items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              type="button"
              onClick={handleFileButtonClick}
              disabled={isUploading || disabled}
              className={ACTION_BUTTON}
              aria-label="Add attachment"
              title="Add attachment"
            >
              {isUploading ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiPaperclip className="h-4 w-4" />}
            </button>
            {onMapDraw && (
              <button
                type="button"
                onClick={onMapDraw}
                disabled={disabled}
                className={ACTION_BUTTON}
                aria-label="Map input"
                title="Map input"
              >
                <GlobeClick className="h-4 w-4 [&_path]:stroke-current" />
              </button>
            )}
            {!hideSend && (
              <button
                type="button"
                className="eai-btn-cta-toolbar"
                onClick={onSend}
                disabled={isSendDisabled}
                aria-label="Send message"
                data-keyboard-dismiss
              >
                Send
                <FiArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
      {error && (
        <div className="mt-2 font-inter text-eai-body-xs text-eai-danger-text-soft" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default AiChatInput;
